import {
  getJobSearchGuidePublicSeo,
  type JobSearchGuideLocale,
} from './job-search-guides-public-seo';

export type IndexationDiscoveryLink = {
  href: string;
  label: string;
};

/** Strip `/en|fi|sv` prefix for Footer `Link` paths (locale added by component). */
export function toLocaleRelativeAppPath(fullPath: string): string {
  const stripped = fullPath.replace(/^\/(en|fi|sv)(?=\/)/, '');
  return stripped.length > 0 ? stripped : fullPath;
}

export function buildJobSearchGuideFooterLinks(
  locale: JobSearchGuideLocale,
): IndexationDiscoveryLink[] {
  const pillar = getJobSearchGuidePublicSeo('finlandPillar', locale);
  const tracker = getJobSearchGuidePublicSeo('applicationTracker', locale);

  if (locale === 'fi') {
    return [
      {
        href: toLocaleRelativeAppPath(pillar.path),
        label: 'Miten löydät töitä Suomessa',
      },
      {
        href: toLocaleRelativeAppPath(tracker.path),
        label: 'Työhakemusten seuranta',
      },
      {
        href: toLocaleRelativeAppPath('/fi/tyoelama/tekoaly-tyonhaussa'),
        label: 'Työnhaku tekoälyllä: Claude ja ChatGPT',
      },
    ];
  }

  if (locale === 'sv') {
    return [
      {
        href: toLocaleRelativeAppPath(pillar.path),
        label: 'Hur hittar man jobb i Finland',
      },
      {
        href: toLocaleRelativeAppPath(tracker.path),
        label: 'Jobbansökningsspårare',
      },
      {
        href: toLocaleRelativeAppPath('/sv/arbetsliv/ai-i-jobbsokningen'),
        label: 'Jobbsökning med Claude eller ChatGPT',
      },
    ];
  }

  return [
    {
      href: toLocaleRelativeAppPath(pillar.path),
      label: 'How to find a job in Finland',
    },
    {
      href: toLocaleRelativeAppPath(tracker.path),
      label: 'Job application tracker',
    },
    {
      href: toLocaleRelativeAppPath('/en/resources/ai-job-search-finland'),
      label: 'Job search with Claude or ChatGPT',
    },
  ];
}

/**
 * The CV template, cover letter and resignation letter pages (TEC-711), footer-linked from every page so crawlers find
 * them: as of 2026-10-06 /fi/tyoelama/cv-pohja and /sv/arbetsliv/cv-mall were unknown to Google.
 */
export function buildCvToolFooterLinks(locale: JobSearchGuideLocale): IndexationDiscoveryLink[] {
  if (locale === 'fi') {
    return [
      { href: toLocaleRelativeAppPath('/fi/tyoelama/cv-pohja'), label: 'Ilmainen CV-pohja' },
      { href: toLocaleRelativeAppPath('/fi/tyoelama/tyohakemus'), label: 'Työhakemus: pohja ja esimerkit' },
      { href: toLocaleRelativeAppPath('/fi/tyoelama/irtisanoutumisilmoitus'), label: 'Irtisanoutumisilmoitus' },
    ];
  }
  if (locale === 'sv') {
    return [
      { href: toLocaleRelativeAppPath('/sv/arbetsliv/cv-mall'), label: 'Gratis CV-mall' },
      { href: toLocaleRelativeAppPath('/sv/arbetsliv/personligt-brev'), label: 'Arbetsansökan: mall och exempel' },
      { href: toLocaleRelativeAppPath('/sv/arbetsliv/uppsagning'), label: 'Uppsägning: mall' },
    ];
  }
  return [
    { href: toLocaleRelativeAppPath('/en/resources/cv-template-finland'), label: 'Finnish CV template' },
    { href: toLocaleRelativeAppPath('/en/resources/cover-letter-finland'), label: 'Cover letter template' },
    { href: toLocaleRelativeAppPath('/en/resources/resignation-letter-finland'), label: 'Resignation letter template' },
  ];
}

export function buildJuniorsFooterLink(locale: JobSearchGuideLocale): IndexationDiscoveryLink {
  if (locale === 'fi') {
    return { href: '/juniors', label: 'Uravinkit juniorille' };
  }
  if (locale === 'sv') {
    return { href: '/juniors', label: 'Karriär för juniorer' };
  }
  return { href: '/juniors', label: 'Careers for juniors' };
}

export function buildEmployerScoreFooterLink(locale: JobSearchGuideLocale): IndexationDiscoveryLink {
  if (locale === 'fi') {
    return { href: '/about-employer-score', label: 'Työnantajapiste-algoritmi' };
  }
  if (locale === 'sv') {
    return { href: '/about-employer-score', label: 'Arbetsgivarpoäng-algoritmen' };
  }
  return { href: '/about-employer-score', label: 'Employer Score methodology' };
}

export function getJobSearchGuideAbsolutePaths(locale: JobSearchGuideLocale): {
  pillar: string;
  tracker: string;
} {
  return {
    pillar: getJobSearchGuidePublicSeo('finlandPillar', locale).path,
    tracker: getJobSearchGuidePublicSeo('applicationTracker', locale).path,
  };
}

export function getJuniorsHubAbsolutePath(locale: JobSearchGuideLocale): string {
  return `/${locale}/juniors`;
}

export function getEmployerScoreAbsolutePath(locale: JobSearchGuideLocale): string {
  return `/${locale}/about-employer-score`;
}
